// src/components/TransactionUploader/index.tsx

import { format, parse } from 'date-fns';
import React, {
    useReducer,
    useCallback,
    useState,
    useMemo,
    useEffect,
} from 'react';
import { doc, collection, addDoc, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { analyzeSingleTransaction } from '../../services/analysis/transactionAnalysis';
import { analyzeDeeperTransaction } from '../../services/analysis/deeperTransactionAnalysis';
import { creaVersamenti } from '../../services/logic/creaVersamenti';
import FileInput from './FileInput';
import TransactionTable from './TransactionTable';
import Thinking from './Thinking';
import { useCsvParser } from '../../hooks/useCsvParser';
import { rowReducer } from './rowReducer';
import {
    RowState,
    TransactionUploaderProps,
    Transaction,
} from './types';
import {
    ActionButton,
    Container,
    TopBar,
} from './TransactionUploader.styles';
import StatsWidget from './StatsWidget';
import {
    Proprietario,
    Inquilino,
    UnitaImmobiliare,
    VersamentoEnriched,
} from '../../services/types/firestoreTypes';
import { computeNextNumPrevAndNumRata } from './utils';

// Helper to parse localized numbers (e.g. "1.234,56")
const parseLocalizedNumber = (value: string): number =>
    Number(value.replace(/\./g, '').replace(/,/g, '.')) || 0;

const TransactionUploader: React.FC<TransactionUploaderProps> = ({
    setAnalysisResult,
    condominioId,
}) => {
    // ─── Lookup lists ────────────────────────────────────────────────
    const [owners, setOwners] = useState<Proprietario[]>([]);
    const [tenants, setTenants] = useState<Inquilino[]>([]);
    const [units, setUnits] = useState<UnitaImmobiliare[]>([]);

    useEffect(() => {
        async function loadLookups() {
            try {
                const [pSnap, iSnap, uSnap] = await Promise.all([
                    getDocs(collection(db, 'condomini', condominioId, 'proprietari')),
                    getDocs(collection(db, 'condomini', condominioId, 'inquilini')),
                    getDocs(collection(db, 'condomini', condominioId, 'unita_immobiliari')),
                ]);
                setOwners(pSnap.docs.map(d => d.data() as Proprietario));
                setTenants(iSnap.docs.map(d => d.data() as Inquilino));
                setUnits(uSnap.docs.map(d => d.data() as UnitaImmobiliare));
            } catch (err) {
                console.error('Error loading lookup lists:', err);
            }
        }
        loadLookups();
    }, [condominioId]);

    // ─── Row state via reducer ────────────────────────────────────────
    const [rows, dispatch] = useReducer(rowReducer, [] as RowState[]);
    const [parsedData, setParsedData] = useState<Transaction[] | null>(null);
    const [isThinking, setIsThinking] = useState(false);

    // ─── “AI thinking” steps ──────────────────────────────────────────
    const steps = useMemo(
        () => [
            'Sto pensando …',
            'Cerco il condominio …',
            'Condominio trovato …',
            'Calendario rate trovato …',
            'Aggiorno con i versamenti già effettuati …',
        ],
        []
    );

    // ─── CSV / XLSX parsing hook ──────────────────────────────────────
    const { error: parseError, handleFileUpload } = useCsvParser(data => {
        setParsedData(data);
        setIsThinking(true);
    });

    // ─── Inline save handler ─────────────────────────────────────────
    // inside src/components/TransactionUploader/index.tsx
    // Updated inline save handler to include condominioId and tipo_rata
    const onSaveInline = useCallback(
        async (
            rowIndex: number,
            vers: VersamentoEnriched,
            versIndex?: number
        ) => {
            const tipoRata = vers.gestione;
            const feeType = tipoRata === 'VO' ? 'GO' :
                tipoRata === 'VS' ? 'GS' : 'RISC';

            const { num_prev, num_rata } = await computeNextNumPrevAndNumRata(
                condominioId,
                feeType as 'GO' | 'GS' | 'RISC',
                vers.person_id,
                vers.ui_id
            );

            const enriched: VersamentoEnriched = {
                ...vers,
                cod_con: condominioId,
                data_reg: new Date().toISOString(),
                num_prev,
                num_rata,
                num_reg: Date.now(),
                progressivo_registrazione: 1,
                tipo_vers: null,
                tipo_rata: feeType, // only for GO/GS
            };

            const existing = rows[rowIndex].versamenti || [];
            const updated = versIndex != null
                ? existing.map((v, i) => (i === versIndex ? enriched : v))
                : [...existing, enriched];

            dispatch({ type: 'SET_VERSAMENTI', index: rowIndex, payload: updated });
            dispatch({ type: 'CLEAR_ERROR', index: rowIndex });
        },
        [rows, condominioId]
    );


    // inside TransactionUploader component

    const handleDeleteInline = useCallback(
        (rowIndex: number, versIndex: number) => {
            const existing = rows[rowIndex].versamenti || [];
            const updated = existing.filter((_, i) => i !== versIndex);
            dispatch({
                type: 'SET_VERSAMENTI',
                index: rowIndex,
                payload: updated
            });
        },
        [rows]
    );




    // ─── Analysis callbacks ──────────────────────────────────────────
    const analyzeOne = useCallback(
        async (idx: number, tx: Transaction) => {
            try {
                const condRef = doc(db, 'condomini', condominioId);
                await addDoc(collection(condRef, 'transazioni_grezze'), tx);
                const res = await analyzeSingleTransaction(tx);
                dispatch({ type: 'SET_ANALYSIS', index: idx, payload: res });
            } catch {
                dispatch({ type: 'SET_ERROR', index: idx, payload: 'Error analyzing transaction' });
            }
        },
        [condominioId]
    );

    const analyzeDeep = useCallback(
        async (idx: number) => {
            const init = rows[idx]?.analysis;
            if (!init) return;
            try {
                const deeperRes = await analyzeDeeperTransaction(init, condominioId);
                dispatch({ type: 'SET_DEEPER', index: idx, payload: deeperRes });
            } catch (err) {
                console.error('Error in deeper analysis for:', idx, condominioId, init);
                console.error(err);
                dispatch({ type: 'SET_ERROR', index: idx, payload: 'Error in deeper analysis' });
            }
        },
        [condominioId, rows]
    );

    const createVersamentiHandler = useCallback(
        async (idx: number) => {
            const { analysis, deeper } = rows[idx] || {};
            if (!analysis || !deeper) return;
            try {
                const vs = await creaVersamenti(condominioId, analysis, deeper);
                dispatch({ type: 'SET_VERSAMENTI', index: idx, payload: vs });
            } catch (err) {
                console.error('Error creating versamenti for:', idx, condominioId, analysis, deeper);
                console.error(err);
                dispatch({ type: 'SET_ERROR', index: idx, payload: 'Error creating versamenti' });
            }
        },
        [condominioId, rows]
    );

    // ─── Sequential “thinking” on file load ──────────────────────────
    const handleThinkingComplete = useCallback(async () => {
        if (!parsedData) return;
        dispatch({ type: 'INIT_ROWS', payload: parsedData });
        for (let i = 0; i < parsedData.length; i++) {
            const tx = parsedData[i];
            const desc = tx['Descrizione Causale ABI/SWIFT'] || '';
            // skip outbound (Dare > 0)
            if (parseLocalizedNumber(tx['Dare']) > 0 || desc.startsWith("078 - ") || desc.startsWith("0Z2 - ") ) continue;

            dispatch({ type: 'SET_PROCESSING', index: i, payload: true });
            try {
                const condRef = doc(db, 'condomini', condominioId);
                await addDoc(collection(condRef, 'transazioni_grezze'), tx);
                const initRes = await analyzeSingleTransaction(tx);
                dispatch({ type: 'SET_ANALYSIS', index: i, payload: initRes });
                const deeperRes = await analyzeDeeperTransaction(initRes, condominioId);
                dispatch({ type: 'SET_DEEPER', index: i, payload: deeperRes });
                const vs = await creaVersamenti(condominioId, initRes, deeperRes);
                dispatch({ type: 'SET_VERSAMENTI', index: i, payload: vs });
            } catch (err) {
                console.error('Error processing row', i, condominioId, tx);
                console.error(err);
                dispatch({ type: 'SET_ERROR', index: i, payload: 'Errore processing row' });
            }
            // mark done
            dispatch({ type: 'SET_PROCESSING', index: i, payload: false });
        }
        setIsThinking(false);
    }, [parsedData, condominioId]);

    // ─── Statistics ─────────────────────────────────────────────────
    const inboundRows = rows.filter(r => parseLocalizedNumber(r.tx['Avere']) > 0);
    const total = inboundRows.length;
    const zero = inboundRows.filter(r => (r.versamenti?.length ?? 0) === 0).length;
    const one = inboundRows.filter(r => (r.versamenti?.length ?? 0) === 1).length;
    const more = inboundRows.filter(r => (r.versamenti?.length ?? 0) > 1).length;

    // ─── Download SQL ────────────────────────────────────────────────
    const downloadVersamenti = useCallback(() => {
        const all = rows.filter(r => r.versamenti).flatMap(r => r.versamenti!);
        if (all.length === 0) {
            alert('Nessun versamento creato da scaricare.');
            return;
        }
        const sqlLines = all.map(v => {
            const table = v.gestione === 'RISC' ? 'Rate_Vers_Risc' : 'Rate_Vers';
            const obj: Record<string, any> = v.gestione === 'RISC'
                ? {
                    cod_con: v.cod_con,
                    cod_ui: v.ui_id,
                    data_reg: v.data_reg,
                    imp_tot: v.imp_tot,
                    num_prev: v.num_prev,
                    num_rata: v.num_rata,
                    num_reg: v.num_reg,
                    progressivo_registrazione: v.progressivo_registrazione,
                    tipo_vers: v.tipo_vers,
                }
                : {
                    cod_con: v.cod_con,
                    cod_condomino: v.person_id,
                    data_reg: v.data_reg,
                    imp_tot: v.imp_tot,
                    num_prev: v.num_prev,
                    num_rata: v.num_rata,
                    num_reg: v.num_reg,
                    progressivo_registrazione: v.progressivo_registrazione,
                    tipo_rata: v.gestione,
                    tipo_vers: v.tipo_vers,
                };
            const cols = Object.keys(obj).map(k => k.toUpperCase());
            const vals = Object.values(obj).map(val =>
                typeof val === 'string' ? `'${val}'` : val
            );
            return `INSERT INTO ${table} (${cols.join(', ')}) VALUES (${vals.join(', ')});`;
        });
        const text = sqlLines.join('\n');
        const blob = new Blob([text], { type: 'application/sql' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'versamenti.sql';
        a.click();
        URL.revokeObjectURL(url);
    }, [rows]);




    const downloadRegistrazioni = useCallback(() => {
        // Flatten [ { vers, dateContabile } … ]
        const all: { v: VersamentoEnriched; dateCont: string }[] = rows
            .flatMap(r =>
                (r.versamenti || []).map(v => ({
                    v,
                    dateCont: r.tx['Data Contabile'] as string || ''
                }))
            );

        if (all.length === 0) {
            alert('Nessuna registrazione creata da scaricare.');
            return;
        }

        // CSV header
        const headers = [
            'cond_risc',
            'tipo_rata_num',
            'cod_condomino',
            'num_prev',
            'Progr',
            'imp_tot',
            'Note',
            'Tipo_Rata',
            'COD_CONDOMINIO',
            'AC_NOM',
            'Data_Documento',
            'Num_registrazione',
        ];

        const rowsCsv = [
            headers.join(';'),
            ...all.map(({ v, dateCont }, idx) => {
                const cond_risc = v.gestione === 'RISC' ? '2' : '1';
                const tipo_rata_num = v.gestione === 'VS' ? '2' : '1';
                const cod_condomino = v.gestione === 'RISC' ? v.ui_id : v.person_id;
                // const cod_condomino = v.person_id;
                const num_prev = v.gestione === 'GO' || v.gestione === 'RISC' ? '0' : String(v.num_prev ?? 1);
                const Progr = String(idx + 1);
                const imp_tot = v.imp_tot.toFixed(2).replace('.', ',');
                const Note = `${v.person_id} ${v.nome}`;
                const Tipo_Rata = v.gestione;
                const COD_CONDOMINIO = condominioId;
                const AC_NOM = v.nome;
                // Now use Data Contabile from the original tx
                // Assume Data Contabile is in an ISO-like string or DD/MM/YYYY
                let Data_Documento = '';
                let parsedDate: Date;
                if (dateCont) {
                    if (dateCont.includes('.')) {
                        // e.g. "30.04.2025"
                        parsedDate = parse(dateCont, 'dd.MM.yyyy', new Date());
                    } else if (dateCont.includes('/')) {
                        // e.g. "04/11/2025" or "4/1/2025"
                        parsedDate = parse(dateCont, 'dd/MM/yyyy', new Date());
                    } else {
                        // fallback for ISO strings
                        parsedDate = new Date(dateCont);
                    }
                }
                Data_Documento = format(parsedDate, 'dd/MM/yyyy');

                const Num_registrazione = '';

                const values = [
                    cond_risc,
                    tipo_rata_num,
                    cod_condomino,
                    num_prev,
                    Progr,
                    imp_tot,
                    Note,
                    Tipo_Rata,
                    COD_CONDOMINIO,
                    AC_NOM,
                    Data_Documento,
                    Num_registrazione,
                ].map(str =>
                    (str.includes(';') || str.includes('"'))
                        ? `"${str.replace(/"/g, '""')}"`
                        : str
                );

                return values.join(';');
            })
        ].join('\r\n');

        const blob = new Blob([rowsCsv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'registrazioni.csv';
        a.click();
        URL.revokeObjectURL(url);
    }, [rows, condominioId]);



    return (
        <Container>
            <TopBar>
                <FileInput onFileSelected={handleFileUpload} parseError={parseError} />
                {/* <ActionButton onClick={downloadVersamenti} disabled={isThinking}>
                    Download Versamenti
                </ActionButton> */}
                <ActionButton onClick={downloadRegistrazioni} disabled={isThinking}>
                    Download Registrazioni
                </ActionButton>
            </TopBar>

            {isThinking && parsedData && (
                <Thinking steps={steps} onComplete={handleThinkingComplete} />
            )}

            {rows.length > 0 && (
                <StatsWidget total={total} zero={zero} one={one} more={more} />
            )}

            {rows.length > 0 && (
                <TransactionTable
                    rows={rows}
                    owners={owners}
                    tenants={tenants}
                    units={units}
                    onAnalyze={i => analyzeOne(i, rows[i].tx)}
                    onDeeper={analyzeDeep}
                    onVersamentiCreated={createVersamentiHandler}
                    onSaveInline={onSaveInline}
                    onDeleteInline={handleDeleteInline}
                />
            )}
        </Container>
    );
};

export default TransactionUploader;
